/**
 * This class sets all the content width "styles"
 */

import { Helper } from '@/services/helper/helper';
import store from '@/store/AppStore';

export default class ContentWidthHelper {
	/**
	 * initializes the variables for the class
	 */
	constructor () {
		this.teaserWidth = 300;
		this.gridColumnGapMaxWidth = 30;
		this.gridColumnGapMinWidth = 20;
		this.gridColumnGapActive = true;
		this.calculatedGridGapWidth = 30;
		this.gridBorderMaxWidth = 30;
		this.gridBorderMinWidth = 10;
		this.calculatedGridBorderWidth = 30;
		this.responsiveGridBorderActive = false;
		this.skyWidth = '130';
		this.rotationSkyMinWidth = '120';
		this.sitebarWidth = '300';
		this.homeframeSkyWidth = '120';
		this.homeframeSitebarMinWidth = '160';
		this.superbannerSitebarMinWidth = '120';
		this.skyRightWidth = 0;
		this.minAdWidth = 0;
		this.clientWidth = document.documentElement.clientWidth;
		this.colCount = 4;
		this.prevColCount = 0;
		this.minCols = 2;
		this.maxCols = 4;
		this.stylesQueue = {};
		this.eventsQueue = {};

		store.watch(
			(state) => state.selectedAd,
			(ad) => {
				document.body.setAttribute('data-ad-type', ad);
				this.setColCase();
			}
		);
	}

	/**
	 * getter for selectedAd
	 */
	get selectedAd () {
		return store.state.selectedAd;
	}

	/**
	 * This function sets the width for the grid border. It is been called on resize and checks if the grid gap is at its maximum.
	 */
	setGridBorderWidth () {
		// react only if grid gap is at it's maximum (first restore gap before border)
		if (this.responsiveGridBorderActive && this.calculatedGridGapWidth === this.gridColumnGapMaxWidth) {
			let possibleGridBorderWidth = (this.clientWidth - this.minAdWidth - this.colCount * this.teaserWidth - (this.colCount - 1) * this.gridColumnGapMaxWidth) / 2;

			if (this.prevColCount > this.colCount) {
				let tempPossibleGridBorderWidth = possibleGridBorderWidth;
				possibleGridBorderWidth = (this.clientWidth - this.minAdWidth - this.prevColCount * this.teaserWidth - (this.prevColCount - 1) * this.gridColumnGapMaxWidth) / 2;

				if (possibleGridBorderWidth < 0) {
					possibleGridBorderWidth = tempPossibleGridBorderWidth;
				}
			}

			this.calculatedGridBorderWidth = Math.max(this.gridBorderMinWidth, Math.min(this.gridBorderMaxWidth, possibleGridBorderWidth));
			this.stylesQueue['--grid-border'] = this.calculatedGridBorderWidth + 'px';
		}
	}

	/**
	 * This function sets the width for all the grid gaps. It is been called on resize and checks if the grid border is at its minimum.
	 * @param {Number} possibleContentWidth - The maximum viewport space that is left after removing the ads and the grid border
	 */
	setGridColumnGapWidth (possibleContentWidth) {
		// this block makes the grid gap smaller if needed as a last option after a possible shrinkage of the grid gap and all the outside ads
		// reacts only if grid border is at it's minimum (as second not first or parallel)
		if (this.gridColumnGapActive && (this.calculatedGridBorderWidth === this.gridBorderMinWidth || !this.responsiveGridBorderActive)) {
			// calculate the new colCount with smaller gaps
			this.colCount = Math.floor((possibleContentWidth + this.gridColumnGapMinWidth) / (this.teaserWidth + this.gridColumnGapMinWidth));
			if (this.colCount > this.maxCols) {
				this.colCount = this.maxCols;
			}

			// calc the biggest possible gap
			let possibleGridGapWidth = (this.clientWidth - this.minAdWidth - this.colCount * this.teaserWidth - 2 * this.calculatedGridBorderWidth) / (this.colCount - 1); // (1310-4*300-20)/3
			this.calculatedGridGapWidth = Math.max(this.gridColumnGapMinWidth, Math.min(this.gridColumnGapMaxWidth, possibleGridGapWidth));

			this.stylesQueue['--grid-column-gap'] = this.calculatedGridGapWidth + 'px';

			// dispatch an event so the slider and others can react to just this change instead of listening to every resize
			this.eventsQueue['gridGapChanged'] = {
				colCount: this.colCount,
				gapWidth: this.calculatedGridGapWidth
			};
		}
	}

	/**
	 * This function calculates the col count for the content (and the rest is for outside ads).
	 */
	setColCase () {
		this.clientWidth = document.documentElement.clientWidth;
		this.setOutsideAdSize();

		// get the possible content width (baseline)
		const possibleContentWidth = this.clientWidth - this.minAdWidth - this.gridBorderMinWidth * 2;
		this.colCount = Math.floor((possibleContentWidth + this.gridColumnGapMinWidth) / (this.teaserWidth + this.gridColumnGapMinWidth));

		if (this.colCount > this.maxCols) {
			this.colCount = this.maxCols;
		}

		this.responsiveGridBorderActive && this.setGridBorderWidth();
		this.gridColumnGapActive && this.setGridColumnGapWidth(possibleContentWidth);
		this.responsiveGridBorderActive && this.setGridBorderWidth(); // a second time because we react to grid gap in this function and in some cases we need to recalc

		this.checkAdOverlap();
		this.stylesQueue['--justify-grid-content'] = 'center';

		if (this.colCount > this.maxCols) {
			this.colCount = this.maxCols;
		} else if (this.colCount < this.minCols) {
			this.stylesQueue['--justify-grid-content'] = 'start';

			if (this.responsiveGridBorderActive) {
				this.stylesQueue['--grid-border'] = this.gridBorderMinWidth + 'px';
			}

			if (this.gridColumnGapActive) {
				this.stylesQueue['--grid-column-gap'] = this.gridColumnGapMinWidth + 'px';

				// dispatch an event so the slider and others can react to just this change instead of listening to every resize
				this.eventsQueue['gridGapChanged'] = {
					colCount: this.colCount,
					gapWidth: this.calculatedGridGapWidth
				};
			}

			this.colCount = this.minCols;
		}

		// col count changed
		if (this.colCount !== this.prevColCount) {
			this.prevColCount = this.colCount;

			document.body.setAttribute('data-col-count', this.colCount);
			document.documentElement.style.setProperty('--col-count', this.colCount);

			this.eventsQueue['colCountChanged'] = {
				colCount: this.colCount
			};
		}

		this.makeStyleChanges();
	}

	/**
	 * This function should not exist but it does... It sets a min-width to the .main-content element if we have a billboard
	 */
	setContentMinWidth () {
		switch (this.selectedAd) {
			case 'billboard': // TODO: remove billboard case as soon as we support 2 cols and less with billboards
				const setBillboardSize = () => {
					const billboardSize = store.state.adData.adsize?.split('x')[0];

					if (billboardSize) {
						document.querySelector('.main-content').style.minWidth = billboardSize + 'px';
					}
				};

				store.state.pageReady ? setBillboardSize() : store.watch((state) => state.pageReady, setBillboardSize);

				break;
			default:
				break;
		}
	}

	/**
	 * This function sets the outside ad min width
	 */
	setOutsideAdSize () {
		let minOutsideAdWidth = 0;

		switch (this.selectedAd?.toLowerCase()) {
			case 'cinematic-wall':
			case 'homeframe':
				this.minAdWidth = +this.homeframeSitebarMinWidth + +this.homeframeSkyWidth;
				minOutsideAdWidth = this.homeframeSitebarMinWidth;
				break;

			case 'wallpaper':
				this.minAdWidth = this.superbannerSitebarMinWidth;
				minOutsideAdWidth = store.state.adData?.right?.fallbackWidth ? store.state.adData.right.fallbackWidth : this.superbannerSitebarMinWidth;
				break;

			case 'bridge':
			case 'double-sitebar':
				this.minAdWidth = this.skyWidth * 2;
				minOutsideAdWidth = this.skyWidth;
				break;

			case 'flagship':
			case 'sitebar':
				this.minAdWidth = this.sitebarWidth;
				minOutsideAdWidth = this.sitebarWidth;
				break;

			case 'video-scroll':
				this.minAdWidth = this.skyWidth * 2;
				minOutsideAdWidth = this.skyWidth;
				break;

			default:
				this.minAdWidth = 0;
				break;
		}

		this.minAdWidth = +this.minAdWidth + this.skyRightWidth;

		this.stylesQueue['--sitebar-width'] = minOutsideAdWidth + 'px';
	}

	/**
	 * This function checks if we have a responsive grid border
	 */
	checkResponsiveGrid () {
		// responsive gridborder active?
		this.responsiveGridBorderActive = this.selectedAd === 'sitebar' || this.selectedAd === 'double-sitebar' || this.selectedAd === 'billboard';

		// min width = default width (we don't go smaller)
		if (!this.responsiveGridBorderActive) {
			this.gridBorderMinWidth = this.gridBorderMaxWidth;
		}

		if (!this.gridColumnGapActive) {
			this.gridColumnGapMinWidth = this.gridColumnGapMaxWidth;
		}
	}

	/**
	 * This function checks if we have are allowed to overlap the outside ads
	 */
	checkAdOverlap () {
		this.setOutsideAdSize();

		// if the ad is allowed to get covered we can show more content
		if (this.selectedAd === 'sitebar' || this.selectedAd === 'flagship') {
			let tempPossibleContentWidth = this.clientWidth - this.minAdWidth - this.gridBorderMaxWidth * 2;
			let tempColCount = Math.floor((tempPossibleContentWidth + this.gridColumnGapMaxWidth) / (this.teaserWidth + this.gridColumnGapMaxWidth));

			if (tempColCount === 2 || (window.PageConfig?.adOverlap4Cols && tempColCount === 3)) {
				let possibleGridGapWidth = (this.clientWidth - this.minAdWidth - (tempColCount + 1) * this.teaserWidth - 2 * this.gridBorderMaxWidth) / tempColCount;
				let tempCalculatedGridGapWidth = Math.max(this.gridColumnGapMinWidth, Math.min(this.gridColumnGapMaxWidth, possibleGridGapWidth));

				let possibleGridBorderWidth = (this.clientWidth - this.minAdWidth - (tempColCount + 1) * this.teaserWidth - tempColCount * this.gridColumnGapMaxWidth) / 2;
				let tempCalculatedGridBorderWidth = Math.max(this.gridBorderMinWidth, Math.min(this.gridBorderMaxWidth, possibleGridBorderWidth));

				if (tempCalculatedGridGapWidth === this.gridColumnGapMinWidth && tempCalculatedGridBorderWidth === this.gridBorderMinWidth) {
					this.minAdWidth = this.minAdWidth / 2;
				}
			}
		}
	}

	/**
	 * This function does all the changes to the content width so there are no more doubled changes to the same thing
	 */
	makeStyleChanges () {
		for (const [key, value] of Object.entries(this.stylesQueue)) {
			document.documentElement.style.setProperty(key, value);
		}

		for (const [eventName, detailObject] of Object.entries(this.eventsQueue)) {
			Helper.fireEvent(eventName, detailObject);
		}

		this.stylesQueue = {};
		this.eventsQueue = {};
	}

	/**
	 * Returns the minimum possible col count of the page for an ad inside of the content
	 */
	getMinColsByAdWidth (adSize) {
		return Math.max(Math.round((+adSize + this.gridColumnGapMinWidth) / (this.teaserWidth + this.gridColumnGapMinWidth)), this.minCols);
	}

	/**
	 * Checks of there is enough space to fit a sky right next to the content
	 */
	checkSkyFits () {
		return document.querySelector('.main-content').clientWidth + +this.rotationSkyMinWidth <= this.clientWidth;
	}

	/**
	 * Returns the maximum available space for a sky right
	 */
	getMaxSkyWidth () {
		return this.clientWidth - document.querySelector('.main-content').clientWidth;
	}
}
